// BracketMobileVertical: Vertical bracket layout for mobile viewports (< 768px).
// Renders bracket as conference funnel: West R1 -> R2 -> CF -> Finals -> East CF -> R2 -> R1
// Each section has one conference and one round with a Hebrew label.

import { motion } from 'framer-motion';
import { BracketSlot } from '@/hooks/use-bracket';
import { BracketPick, BracketPicks } from '@/types/bracket';
import BracketMatchupSlot from '@/components/BracketMatchupSlot';

interface SeriesOddsRow {
  id: string;
  series_id: string | null;
  team: string;
  outcome: string;
  team_wins: number;
  opponent_wins: number;
  american_odds: number;
  decimal_odds: number | null;
  multiplier: number | null;
}

interface BracketMobileVerticalProps {
  westR1: BracketSlot[];
  westR2: BracketSlot[];
  westCF: BracketSlot[];
  finals: BracketSlot[];
  eastCF: BracketSlot[];
  eastR2: BracketSlot[];
  eastR1: BracketSlot[];
  picks: BracketPicks;
  oddsMap: Record<string, SeriesOddsRow[]>;
  isLocked: boolean;
  isViewingOther: boolean;
  onPickWinner: (slotId: string, winner: string) => void;
  onTapForScore: (slotId: string) => void;
}

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.05 },
  },
};

const SectionDivider = ({ label, accent = false }: { label: string; accent?: boolean }) => (
  <div className="flex items-center gap-3 my-6">
    <div className={`flex-1 h-px ${accent ? 'bg-accent/30' : 'bg-border'}`} />
    <span className="text-xs uppercase tracking-[0.2em] text-accent font-bold whitespace-nowrap">
      {label}
    </span>
    <div className={`flex-1 h-px ${accent ? 'bg-accent/30' : 'bg-border'}`} />
  </div>
);

const BracketMobileVertical = ({
  westR1,
  westR2,
  westCF,
  finals,
  eastCF,
  eastR2,
  eastR1,
  picks,
  isLocked,
  isViewingOther,
  onPickWinner,
  onTapForScore,
}: BracketMobileVerticalProps) => {
  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="px-3 pb-8 overflow-hidden"
    >
      {/* ── Section 1: West R1 ──────────────────────────────── */}
      <SectionDivider label="סיבוב 1 – מערב" />
      <div className="grid grid-cols-2 gap-3">
        {westR1.map(slot => (
          <BracketMatchupSlot
            key={slot.slotId}
            slotId={slot.slotId}
            round={slot.round}
            teamHome={slot.teamHome}
            teamAway={slot.teamAway}
            pick={picks[slot.slotId]}
            isLocked={isLocked}
            isViewingOther={isViewingOther}
            isFinals={false}
            isPropagated={slot.fedBy !== null && (slot.teamHome !== null || slot.teamAway !== null)}
            onPickWinner={onPickWinner}
            onTapForScore={onTapForScore}
          />
        ))}
      </div>

      {/* ── Section 2: West R2 ──────────────────────────────── */}
      <SectionDivider label="סיבוב 2 – מערב" />
      <div className="grid grid-cols-2 gap-3">
        {westR2.map(slot => (
          <BracketMatchupSlot
            key={slot.slotId}
            slotId={slot.slotId}
            round={slot.round}
            teamHome={slot.teamHome}
            teamAway={slot.teamAway}
            pick={picks[slot.slotId]}
            isLocked={isLocked}
            isViewingOther={isViewingOther}
            isFinals={false}
            isPropagated={slot.fedBy !== null && (slot.teamHome !== null || slot.teamAway !== null)}
            onPickWinner={onPickWinner}
            onTapForScore={onTapForScore}
          />
        ))}
      </div>

      {/* ── Section 3: West CF ──────────────────────────────── */}
      <SectionDivider label="גמר מערבי" />
      <div className="flex justify-center">
        <div className="w-full max-w-[200px]">
          {westCF.map(slot => (
            <BracketMatchupSlot
              key={slot.slotId}
              slotId={slot.slotId}
              round={slot.round}
              teamHome={slot.teamHome}
              teamAway={slot.teamAway}
              pick={picks[slot.slotId]}
              isLocked={isLocked}
              isViewingOther={isViewingOther}
              isFinals={false}
              isPropagated={slot.fedBy !== null && (slot.teamHome !== null || slot.teamAway !== null)}
              onPickWinner={onPickWinner}
              onTapForScore={onTapForScore}
            />
          ))}
        </div>
      </div>

      {/* ── Section 4: Finals (gold accent) ─────────────────── */}
      <SectionDivider label="הגמר" accent />
      <div className="flex justify-center">
        <div className="w-full max-w-[220px] relative">
          {/* Radial glow behind Finals card */}
          <div
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                'radial-gradient(ellipse 120% 80% at 50% 50%, rgba(234,88,12,0.08) 0%, transparent 70%)',
            }}
          />
          <div className="relative z-[1]">
            {finals.map(slot => (
              <BracketMatchupSlot
                key={slot.slotId}
                slotId={slot.slotId}
                round={slot.round}
                teamHome={slot.teamHome}
                teamAway={slot.teamAway}
                pick={picks[slot.slotId]}
                isLocked={isLocked}
                isViewingOther={isViewingOther}
                isFinals={true}
                isPropagated={slot.fedBy !== null && (slot.teamHome !== null || slot.teamAway !== null)}
                onPickWinner={onPickWinner}
                onTapForScore={onTapForScore}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Section 5: East CF ──────────────────────────────── */}
      <SectionDivider label="גמר מזרחי" />
      <div className="flex justify-center">
        <div className="w-full max-w-[200px]">
          {eastCF.map(slot => (
            <BracketMatchupSlot
              key={slot.slotId}
              slotId={slot.slotId}
              round={slot.round}
              teamHome={slot.teamHome}
              teamAway={slot.teamAway}
              pick={picks[slot.slotId]}
              isLocked={isLocked}
              isViewingOther={isViewingOther}
              isFinals={false}
              isPropagated={slot.fedBy !== null && (slot.teamHome !== null || slot.teamAway !== null)}
              onPickWinner={onPickWinner}
              onTapForScore={onTapForScore}
            />
          ))}
        </div>
      </div>

      {/* ── Section 6: East R2 ──────────────────────────────── */}
      <SectionDivider label="סיבוב 2 – מזרח" />
      <div className="grid grid-cols-2 gap-3">
        {eastR2.map(slot => (
          <BracketMatchupSlot
            key={slot.slotId}
            slotId={slot.slotId}
            round={slot.round}
            teamHome={slot.teamHome}
            teamAway={slot.teamAway}
            pick={picks[slot.slotId]}
            isLocked={isLocked}
            isViewingOther={isViewingOther}
            isFinals={false}
            isPropagated={slot.fedBy !== null && (slot.teamHome !== null || slot.teamAway !== null)}
            onPickWinner={onPickWinner}
            onTapForScore={onTapForScore}
          />
        ))}
      </div>

      {/* ── Section 7: East R1 ──────────────────────────────── */}
      <SectionDivider label="סיבוב 1 – מזרח" />
      <div className="grid grid-cols-2 gap-3">
        {eastR1.map(slot => (
          <BracketMatchupSlot
            key={slot.slotId}
            slotId={slot.slotId}
            round={slot.round}
            teamHome={slot.teamHome}
            teamAway={slot.teamAway}
            pick={picks[slot.slotId]}
            isLocked={isLocked}
            isViewingOther={isViewingOther}
            isFinals={false}
            isPropagated={slot.fedBy !== null && (slot.teamHome !== null || slot.teamAway !== null)}
            onPickWinner={onPickWinner}
            onTapForScore={onTapForScore}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default BracketMobileVertical;
